/**
 * Admin · Pending withdrawals.
 *
 * Lists every withdrawal sitting in manual-review state with the user, bank
 * payout details, and the net amount to transfer. Each row has Settle (mark
 * paid after off-platform bank transfer) and Reject (reverse the hold and
 * return funds to the user's wallet) actions.
 */

import { api, ApiError } from '../api.js';
import { auth } from '../auth.js';
import { escape } from '../components/form.js';
import { toast } from '../components/toast.js';
import { navigate } from '../router.js';
import { formatKobo } from '../wallet.js';

const PAGE_SIZE = 25;

const state = {
  offset: 0,
  total: 0,
  hasMore: false,
};

export async function renderAdminWithdrawals(root) {
  const me = auth.getMe();
  if (!me?.is_admin) {
    root.innerHTML = `
      <div class="appbar"><div class="appbar-title">Withdrawals (admin)</div></div>
      <div class="pad"><div class="card" style="color:var(--color-danger)">Admins only.</div></div>
    `;
    return;
  }

  root.innerHTML = `
    <div class="appbar">
      <a href="/admin" data-link class="appbar-back" aria-label="Back">‹</a>
      <div class="appbar-title">Pending withdrawals</div>
      <div class="appbar-spacer"></div>
      <span class="status-pill bad">ADMIN</span>
    </div>
    <div class="pad" id="wd-page">
      <div class="card" style="padding:12px;">
        <div class="muted" style="font-size:12px;line-height:1.5;">
          These users have withdrawn from their wallet. Funds are already held;
          send the <b style="color:#fff;">Net</b> amount via your bank app, then
          tap <b style="color:#fff;">Settle</b>. Tap <b style="color:#fff;">Reject</b>
          to return the held funds to the user's wallet.
        </div>
      </div>

      <div class="card" style="padding:12px;margin-top:10px;" id="wd-card">
        <div id="wd-summary" class="muted" style="font-size:11px;margin-bottom:6px;"></div>
        <div id="wd-list"><div class="muted">Loading…</div></div>
        <div id="wd-pager" class="row" style="justify-content:space-between;margin-top:10px;"></div>
      </div>
    </div>
  `;

  await load();
}

async function load() {
  const list = document.getElementById('wd-list');
  const pager = document.getElementById('wd-pager');
  const summary = document.getElementById('wd-summary');
  list.innerHTML = `<div class="muted">Loading…</div>`;
  pager.innerHTML = '';
  try {
    const params = new URLSearchParams({
      limit: String(PAGE_SIZE),
      offset: String(state.offset),
    });
    const page = await api(`/admin/withdrawals?${params.toString()}`);
    state.total = page.total;
    state.hasMore = page.has_more;
    if (page.total === 0) {
      summary.textContent = '';
      list.innerHTML = `<div class="muted" style="padding:14px 0;">Nothing waiting — all caught up.</div>`;
      return;
    }
    summary.textContent =
      `${page.total.toLocaleString('en-NG')} pending · showing ${page.offset + 1}–${page.offset + page.items.length}`;
    renderList(list, page.items);
    renderPager(pager);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      navigate('/login');
      return;
    }
    toast.fromError(err);
    list.innerHTML = `<div style="color:var(--color-danger)">${escape(err.message || 'Could not load withdrawals.')}</div>`;
  }
}

function renderList(host, items) {
  host.innerHTML = `
    <div style="display:grid;gap:10px;">
      ${items.map(renderRow).join('')}
    </div>
  `;
  host.querySelectorAll('[data-action]').forEach((btn) => {
    btn.addEventListener('click', () => act(btn));
  });
  host.querySelectorAll('[data-copy]').forEach((btn) => {
    btn.addEventListener('click', () => {
      navigator.clipboard?.writeText(btn.dataset.copy).then(
        () => toast.success(`Copied ${btn.dataset.copyLabel || ''}`.trim()),
        () => toast.error('Copy failed.'),
      );
    });
  });
}

function renderRow(r) {
  return `
    <div class="card" data-txn="${escape(r.txn_id)}" style="padding:12px;background:var(--surf-2);">
      <div class="row" style="justify-content:space-between;align-items:flex-start;gap:10px;">
        <div style="min-width:0;flex:1;">
          <div style="font-size:13px;color:#fff;">
            <b>${escape(r.user_email)}</b>
            ${r.user_username ? `<span class="muted"> · @${escape(r.user_username)}</span>` : ''}
          </div>
          <div class="muted" style="font-size:11px;margin-top:2px;">
            Requested ${formatWhen(r.created_at)} · txn ${escape(r.txn_id.slice(0, 8))}
          </div>
        </div>
        <div style="text-align:right;white-space:nowrap;">
          <div style="font-family:var(--font-display);font-size:18px;font-weight:700;color:#fff;">
            ${escape(formatKobo(r.net_kobo))}
          </div>
          <div class="muted" style="font-size:11px;">
            gross ${escape(formatKobo(r.amount_kobo))} · fee ${escape(formatKobo(r.fee_kobo))}
          </div>
        </div>
      </div>

      <div style="margin-top:10px;padding:8px 10px;background:#0E0B1F;border-radius:8px;font-size:12.5px;">
        <div class="row" style="justify-content:space-between;gap:8px;">
          <span class="muted">Bank</span>
          <span style="color:#fff;">${escape(r.bank_name)} (${escape(r.bank_code)})</span>
        </div>
        <div class="row" style="justify-content:space-between;gap:8px;margin-top:4px;">
          <span class="muted">Account</span>
          <span style="color:#fff;font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;">
            ${escape(r.account_number || '—')}
            ${r.account_number ? `<button type="button" class="button-secondary button-inline" style="margin-left:6px;padding:1px 6px;font-size:10px;" data-copy="${escape(r.account_number)}" data-copy-label="account number">copy</button>` : ''}
          </span>
        </div>
        <div class="row" style="justify-content:space-between;gap:8px;margin-top:4px;">
          <span class="muted">Name</span>
          <span style="color:#fff;">${escape(r.account_name || '—')}</span>
        </div>
      </div>

      <div class="row" style="gap:8px;margin-top:10px;">
        <button type="button" class="btn sm gold" data-action="settle" style="flex:1;">
          Settle (paid ${escape(formatKobo(r.net_kobo))})
        </button>
        <button type="button" class="button-secondary button-inline" data-action="reject">
          Reject
        </button>
      </div>
    </div>
  `;
}

async function act(btn) {
  const card = btn.closest('[data-txn]');
  if (!card) return;
  const txnId = card.dataset.txn;
  const action = btn.dataset.action;
  const prompts = {
    settle: 'Confirm: transfer was sent off-platform? (optional admin note)',
    reject: 'Reject this withdrawal? Funds will be returned to the user. (optional reason)',
  };
  const note = window.prompt(prompts[action], '');
  if (note === null) return; // user cancelled
  card.querySelectorAll('button').forEach((b) => (b.disabled = true));
  try {
    await api(`/admin/withdrawals/${encodeURIComponent(txnId)}/${action}`, {
      method: 'POST',
      body: { note: note || null },
    });
    toast.success(action === 'settle' ? 'Marked as paid.' : 'Withdrawal rejected, funds returned.');
    await load();
  } catch (err) {
    toast.fromError(err);
    card.querySelectorAll('button').forEach((b) => (b.disabled = false));
  }
}

function renderPager(host) {
  const onFirst = state.offset === 0;
  host.innerHTML = `
    <button type="button" class="button-secondary button-inline" id="prev-page" ${onFirst ? 'disabled' : ''}>← Prev</button>
    <div class="muted" style="font-size:11px;">Page ${Math.floor(state.offset / PAGE_SIZE) + 1}</div>
    <button type="button" class="button-secondary button-inline" id="next-page" ${state.hasMore ? '' : 'disabled'}>Next →</button>
  `;
  host.querySelector('#prev-page').addEventListener('click', () => {
    state.offset = Math.max(0, state.offset - PAGE_SIZE);
    void load();
  });
  host.querySelector('#next-page').addEventListener('click', () => {
    state.offset += PAGE_SIZE;
    void load();
  });
}

function formatWhen(iso) {
  if (!iso) return 'just now';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const diffSec = Math.max(0, Math.round((Date.now() - d.getTime()) / 1000));
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return d.toLocaleString('en-NG', { timeZone: 'Africa/Lagos', dateStyle: 'medium', timeStyle: 'short' });
}
